import React, { useMemo, useState } from 'react';
import { toolboxService } from '../services/api';

const LANGUAGE_OPTIONS = ['en_US', 'zh_HK', 'zh_CN'];

const applyTemplate = (template, fileName, mediaType) =>
  (template || '')
    .replaceAll('{{FILE_NAME}}', fileName)
    .replaceAll('{{TYPE}}', mediaType);

const MediaBulkUploadTool = () => {
  const [clinicId, setClinicId] = useState('');
  const [zipFile, setZipFile] = useState(null);
  const [defaultLanguage, setDefaultLanguage] = useState('en_US');
  const [defaultNeedQA, setDefaultNeedQA] = useState(true);
  const [questionPattern, setQuestionPattern] = useState('{{FILE_NAME}} [{{TYPE}}]');
  const [answerPattern, setAnswerPattern] = useState(
    'Use send_media tool to send the image which media name is: {{FILE_NAME}} [{{TYPE}}]'
  );
  const [previewLoading, setPreviewLoading] = useState(false);
  const [commitLoading, setCommitLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [previewItems, setPreviewItems] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [limits, setLimits] = useState(null);
  const [commitResult, setCommitResult] = useState(null);

  const canPreview = clinicId.trim() && zipFile && !previewLoading;
  const canCommit = clinicId.trim() && zipFile && previewItems.length > 0 && !commitLoading;

  const qaCount = useMemo(
    () => previewItems.filter((item) => item.need_qa_knowledge).length,
    [previewItems]
  );

  const updateItem = (rowKey, patch) => {
    setPreviewItems((prev) =>
      prev.map((item) => (item.row_key === rowKey ? { ...item, ...patch } : item))
    );
  };

  const applyPatternToAllRows = () => {
    setPreviewItems((prev) =>
      prev.map((item) => ({
        ...item,
        question: applyTemplate(questionPattern, item.file_name, item.type),
        answer: applyTemplate(answerPattern, item.file_name, item.type),
      }))
    );
  };

  const applyNeedQAToAllRows = (needQA) => {
    setPreviewItems((prev) => prev.map((item) => ({ ...item, need_qa_knowledge: needQA })));
  };

  const applyLanguageToAllRows = (language) => {
    setPreviewItems((prev) => prev.map((item) => ({ ...item, language })));
  };

  const handlePreview = async () => {
    setError('');
    setSuccessMessage('');
    setCommitResult(null);

    if (!clinicId.trim()) {
      setError('Clinic ID is required.');
      return;
    }
    if (!zipFile) {
      setError('Please select a zip file.');
      return;
    }

    setPreviewLoading(true);
    const response = await toolboxService.mediaBulkPreview(clinicId.trim(), zipFile);
    setPreviewLoading(false);

    if (!response.success) {
      setError(response.error || 'Failed to preview zip file.');
      return;
    }

    const preparedItems = (response.items || []).map((item) => ({
      ...item,
      need_qa_knowledge: defaultNeedQA,
      language: defaultLanguage,
      question: applyTemplate(questionPattern, item.file_name, item.type),
      answer: applyTemplate(answerPattern, item.file_name, item.type),
    }));

    setPreviewItems(preparedItems);
    setWarnings(response.warnings || []);
    setLimits(response.limits || null);
    setSuccessMessage(`Preview ready: ${preparedItems.length} supported files detected.`);
  };

  const handleCommit = async () => {
    setError('');
    setSuccessMessage('');
    setCommitResult(null);

    const rows = previewItems.map((item) => ({
      row_key: item.row_key,
      need_qa_knowledge: item.need_qa_knowledge,
      question: item.question,
      answer: item.answer,
      language: item.language,
    }));

    setCommitLoading(true);
    const response = await toolboxService.mediaBulkCommit(clinicId.trim(), zipFile, rows);
    setCommitLoading(false);

    if (!response.success && !response.summary) {
      setError(response.error || 'Failed to run bulk commit.');
      return;
    }

    setCommitResult(response);
    setSuccessMessage('Bulk upload completed. Review summary below.');
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Bulk Upload Media</h1>
          <p className="text-gray-600 mt-2">
            Step 1 uploads a zip package (recommended over folder upload) and validates supported files.
            Step 2 lets you preview and edit QA templates before import.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Clinic ID</label>
            <input
              type="text"
              value={clinicId}
              onChange={(e) => setClinicId(e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="e.g. DEMO_CAS_SUPPORT"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Zip Package</label>
            <input
              type="file"
              accept=".zip,application/zip"
              onChange={(e) => setZipFile(e.target.files?.[0] || null)}
              className="w-full border rounded px-3 py-2"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Language (Step 1)</label>
            <select
              value={defaultLanguage}
              onChange={(e) => setDefaultLanguage(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              {LANGUAGE_OPTIONS.map((language) => (
                <option key={language} value={language}>
                  {language}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2 flex items-end">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={defaultNeedQA}
                onChange={(e) => setDefaultNeedQA(e.target.checked)}
              />
              Need QA for all items by default
            </label>
          </div>
        </div>

        <div className="bg-gray-50 border rounded p-4 text-sm text-gray-700 space-y-1">
          <div>Supported formats: images (`jpg/jpeg/png/webp`), documents (`pdf`), videos (`mp4`).</div>
          <div>Default language: `en_US`. Available: `zh_HK`, `zh_CN`, `en_US`.</div>
          <div>Fixed QA tag: `media`.</div>
          {limits && (
            <div>
              Limits: single file {limits.single_file_mb}MB, zip {limits.zip_file_mb}MB, max {limits.max_files} files.
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={handlePreview}
            disabled={!canPreview}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {previewLoading ? 'Preparing Preview...' : 'Step 1: Preview Zip'}
          </button>
          <button
            type="button"
            onClick={handleCommit}
            disabled={!canCommit}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
          >
            {commitLoading ? 'Committing...' : 'Step 2: Commit Upload'}
          </button>
          <div className="text-sm text-gray-600 self-center">
            QA rows selected: <span className="font-semibold">{qaCount}</span>
          </div>
        </div>

        {error && <div className="p-3 bg-red-100 text-red-700 rounded">{error}</div>}
        {successMessage && <div className="p-3 bg-green-100 text-green-700 rounded">{successMessage}</div>}

        {warnings.length > 0 && (
          <div className="border border-yellow-300 bg-yellow-50 rounded p-4">
            <h3 className="font-semibold text-yellow-900 mb-2">Skipped Files</h3>
            <ul className="space-y-1 text-sm text-yellow-800">
              {warnings.map((warning, index) => (
                <li key={`${warning.file_path}-${index}`}>
                  {warning.file_path}: {warning.reason}
                </li>
              ))}
            </ul>
          </div>
        )}

        {previewItems.length > 0 && (
          <div className="space-y-4">
            <div className="border rounded p-4 bg-gray-50 space-y-4">
              <h3 className="font-semibold text-gray-900">Customize Question/Answer Pattern</h3>
              <div className="text-xs text-gray-600">
                Variables: <code>{'{{FILE_NAME}}'}</code>, <code>{'{{TYPE}}'}</code>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Question Pattern</label>
                  <textarea
                    rows={2}
                    value={questionPattern}
                    onChange={(e) => setQuestionPattern(e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Answer Pattern</label>
                  <textarea
                    rows={2}
                    value={answerPattern}
                    onChange={(e) => setAnswerPattern(e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={applyPatternToAllRows}
                  className="px-3 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm"
                >
                  Apply Pattern to All
                </button>
                <button
                  type="button"
                  onClick={() => applyNeedQAToAllRows(true)}
                  className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                >
                  Need QA for All
                </button>
                <button
                  type="button"
                  onClick={() => applyNeedQAToAllRows(false)}
                  className="px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm"
                >
                  No QA for All
                </button>
                <select
                  value={defaultLanguage}
                  onChange={(e) => {
                    const language = e.target.value;
                    setDefaultLanguage(language);
                    applyLanguageToAllRows(language);
                  }}
                  className="border rounded px-3 py-2 text-sm"
                >
                  {LANGUAGE_OPTIONS.map((language) => (
                    <option key={language} value={language}>
                      Set all language: {language}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
            <table className="min-w-full border bg-white">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border px-3 py-2 text-left">QA</th>
                  <th className="border px-3 py-2 text-left">FILENAME</th>
                  <th className="border px-3 py-2 text-left">TYPE</th>
                  <th className="border px-3 py-2 text-left">QUESTION</th>
                  <th className="border px-3 py-2 text-left">ANSWER</th>
                  <th className="border px-3 py-2 text-left">LANGUAGE</th>
                </tr>
              </thead>
              <tbody>
                {previewItems.map((item) => (
                  <tr key={item.row_key} className="align-top">
                    <td className="border px-3 py-2">
                      <div className="flex flex-col gap-2">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={item.need_qa_knowledge}
                            onChange={(e) =>
                              updateItem(item.row_key, { need_qa_knowledge: e.target.checked })
                            }
                          />
                          need QA
                        </label>
                        <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800 w-fit">
                          media
                        </span>
                      </div>
                    </td>
                    <td className="border px-3 py-2 text-sm">
                      <div className="font-medium">{item.file_name}</div>
                      <div className="text-xs text-gray-500">{item.file_path}</div>
                    </td>
                    <td className="border px-3 py-2 text-sm">{item.type}</td>
                    <td className="border px-3 py-2">
                      <textarea
                        rows={3}
                        value={item.question}
                        onChange={(e) => updateItem(item.row_key, { question: e.target.value })}
                        className="w-full border rounded px-2 py-1 text-sm"
                      />
                    </td>
                    <td className="border px-3 py-2">
                      <textarea
                        rows={3}
                        value={item.answer}
                        onChange={(e) => updateItem(item.row_key, { answer: e.target.value })}
                        className="w-full border rounded px-2 py-1 text-sm"
                      />
                    </td>
                    <td className="border px-3 py-2">
                      <select
                        value={item.language}
                        onChange={(e) => updateItem(item.row_key, { language: e.target.value })}
                        className="w-full border rounded px-2 py-1 text-sm"
                      >
                        {LANGUAGE_OPTIONS.map((language) => (
                          <option key={language} value={language}>
                            {language}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {commitResult?.summary && (
          <div className="border rounded p-4 bg-gray-50">
            <h3 className="font-semibold text-gray-900 mb-2">Import Summary</h3>
            <div className="grid md:grid-cols-3 gap-2 text-sm">
              <div>Total rows: {commitResult.summary.total_rows}</div>
              <div>Media created: {commitResult.summary.media_created}</div>
              <div>Media failed: {commitResult.summary.media_failed}</div>
              <div>QA requested: {commitResult.summary.qa_requested}</div>
              <div>QA created: {commitResult.summary.qa_created}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaBulkUploadTool;
